/**
 * Razorpay checkout chassis. Lazy-loads SDK script. Key injected at build.
 * Tiers shared across all empire-apps pricing.
 */

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => { open: () => void; close: () => void };
    __RAZORPAY_KEY__?: string;
    __SARVAM_KEY__?: string;
  }
}

export interface PricingTier {
  id: 'free' | 'pro' | 'family' | 'b2b';
  name: string;
  inr_annual: number;
  inr_monthly_eq: number;
  positioning_hi: string;
  positioning_en: string;
  features: string[];
}

export const ARAMGAH_TIERS: readonly PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    inr_annual: 0,
    inr_monthly_eq: 0,
    positioning_hi: 'haq se · sab ke liye',
    positioning_en: 'baseline rest dossier, on-device',
    features: ['30-night tier', 'chronotype', 'consistency score', 'on-device encryption'],
  },
  {
    id: 'pro',
    name: 'Pro',
    inr_annual: 999,
    inr_monthly_eq: 83,
    positioning_hi: 'ek chai ke daam mein saara saal',
    positioning_en: 'one tea a month for a year of better rest',
    features: ['PPG HRV', 'ayurveda dosha window', 'lullaby library', '90-day chronotype regression', 'Apple HealthKit import'],
  },
  {
    id: 'family',
    name: 'Family',
    inr_annual: 2999,
    inr_monthly_eq: 250,
    positioning_hi: 'parivaar ki neend, ek hisaab',
    positioning_en: 'four sleepers, one rolled-up dossier',
    features: ['4 members', 'shared bedtime story library', 'family sleep-debt view', 'priority support'],
  },
  {
    id: 'b2b',
    name: 'Studio / Clinic',
    inr_annual: 0,
    inr_monthly_eq: 0,
    positioning_hi: 'sleep clinic / ayurveda practitioner',
    positioning_en: 'white-label for sleep clinics + ayurveda practitioners',
    features: ['unlimited patients', 'practitioner dashboard', 'branded reports', 'contract on request'],
  },
];

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: 'INR';
  name: string;
  description: string;
  handler: (response: { razorpay_payment_id: string }) => void;
  modal?: { ondismiss: () => void };
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color: string };
}

async function loadRazorpayScript(): Promise<void> {
  if (window.Razorpay) return;
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('razorpay-script-failed'));
    document.head.appendChild(s);
  });
}

export async function openCheckout(tier: PricingTier, onPaid: (paymentId: string) => void): Promise<void> {
  if (tier.inr_annual === 0) {
    onPaid('free');
    return;
  }
  const key = 'rzp_live_SVzHpgz5EgB4bk';
  await loadRazorpayScript();
  const Rzp = window.Razorpay;
  if (!Rzp) throw new Error('razorpay-unavailable');
  const rzp = new Rzp({
    key,
    amount: tier.inr_annual * 100,
    currency: 'INR',
    name: 'Aramgah',
    description: `${tier.name} · ${tier.inr_annual} INR / year`,
    handler: (response) => onPaid(response.razorpay_payment_id),
    modal: { ondismiss: () => { /* user closed checkout */ } },
    theme: { color: '#0c0a1f' },
    notes: { brand: 'aramgah', tier: tier.id },
  });
  rzp.open();
}
